import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Unit } from "../../types/company";
import { BLOCKING_STATUSES, TODAY } from "../../data/company";
import {
  addDaysIso,
  addListing,
  availableCount,
  driverName,
  findDriver,
  findListing,
  findUnit,
  listingName,
  pickupAddress,
  publicStatus,
  setUnitStatus,
  unitsOf,
} from "../../lib/company";
import { useCompany } from "../../lib/companyContext";
import { updateListing } from "../../lib/companyApi";
import { cn, eur, num, shortDate } from "../../lib/format";
import { useT } from "../../i18n";
import { ApiError } from "../../lib/api";
import { usePublish } from "../../components/company/usePublish";
import { BlockDatesModal } from "../../components/company/modals";
import { ActionButton, BackLink, Card, CardTitle, StatusBadge } from "../../components/company/ui";

const TABLE_COLS = "grid grid-cols-[100px_110px_90px_110px_minmax(0,1fr)_210px] gap-2.5";
const whiteLink =
  "flex h-9.5 items-center rounded bg-white px-3.5 text-[13px] font-medium text-ink no-underline hover:bg-[#FAFAFA]";

export default function CompanyListingDetail() {
  const t = useT();
  const d = t.company.listingDetail;
  const { id = "" } = useParams();
  const { state, run, toast } = useCompany();
  const { publish, modal: publishModal } = usePublish();
  const [blockUnit, setBlockUnit] = useState<Unit | null>(null);
  const [newCode, setNewCode] = useState("");
  const [newPlate, setNewPlate] = useState("");
  const [adding, setAdding] = useState(false);

  const listing = findListing(state, id);
  if (!listing) {
    const L = t.company.listings;
    return <p className="text-sm text-muted">{state.synced ? L.notFound : L.loading}</p>;
  }

  const units = unitsOf(state, id);
  const pub = publicStatus(state, listing);
  const month = addDaysIso(TODAY, 30);
  const nextCode = `${units[0]?.code.split("-")[0] ?? "VEH"}-${String(units.length + 1).padStart(3, "0")}`;
  const bookings = state.bookings
    .filter((b) => b.listingId === id)
    .sort((a, b) => b.start.localeCompare(a.start));

  const stats: [string, number][] = [
    [d.stats.units, units.length],
    [d.stats.availableToday, availableCount(state, id, TODAY, addDaysIso(TODAY, 1))],
    [d.stats.availableRange(shortDate(TODAY), shortDate(month)), availableCount(state, id, TODAY, month)],
    [d.stats.inMaintenance, units.filter((u) => u.status === "maintenance").length],
  ];

  /** What the unit is doing now or next. */
  const occupation = (u: Unit) => {
    const next = state.bookings
      .filter((b) => b.unitId === u.id && BLOCKING_STATUSES.includes(b.status) && b.end >= TODAY)
      .sort((a, b) => a.start.localeCompare(b.start));
    const block = u.blocks.find((b) => b.end >= TODAY);
    const more = next.length > 1 ? ` (+${next.length - 1})` : "";
    if (next[0]) {
      const b = next[0];
      return b.start <= TODAY
        ? d.rented(b.id, shortDate(b.end)) + more
        : d.booked(shortDate(b.start), shortDate(b.end), b.id) + more;
    }
    if (block) return d.blocked(shortDate(block.start), shortDate(block.end), block.reason);
    return d.free;
  };

  /** PUT the full vehicle list (existing rows keep their _id) plus the new one. */
  const add = async () => {
    const code = (newCode.trim() || nextCode).toUpperCase();
    setAdding(true);
    try {
      const saved = await updateListing(id, {
        units: [
          ...units.map((u) => ({ _id: u.id, code: u.code, plate: u.plate })),
          { code, plate: newPlate.trim() },
        ],
      });
      run(addListing, saved.listing, saved.units);
      toast.success(d.added(code));
      setNewCode("");
      setNewPlate("");
    } catch (err) {
      // e.g. "Vehicle code X already exists in your fleet"
      toast.error(err instanceof ApiError && err.errors?.units ? err.errors.units : err);
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <BackLink to="/company/listings">{t.company.listings.title}</BackLink>
      <div className="mt-2.5 mb-4.5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="m-0 text-[26px] font-medium">{listingName(listing)}</h1>
          <div className="mt-1 text-sm text-muted">
            {listing.version} · {listing.city} · {eur(listing.weekly)}
            {t.common.perWeekShort} · {eur(listing.monthly)}
            {t.common.perMonth}
          </div>
          {pickupAddress(listing) && (
            <div className="mt-1 text-[13px] text-muted">
              {d.pickup} · <span className="text-ink">{pickupAddress(listing)}</span>
            </div>
          )}
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <StatusBadge kind="publish" status={listing.publish} />
            <span className={cn("text-xs", pub.visible ? "text-tone-green-fg" : "text-faint")}>{pub.text}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {pub.visible && (
            <Link to="/vehicles" className={whiteLink}>
              {d.viewPublic}
            </Link>
          )}
          <Link to={`/company/listings/${id}/edit`} className={whiteLink}>
            {t.common.edit}
          </Link>
          {(listing.publish === "draft" || listing.publish === "paused") && (
            <ActionButton variant="primary" className="h-9.5" onClick={() => publish(id)}>
              {t.common.publish}
            </ActionButton>
          )}
        </div>
      </div>

      <div className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {stats.map(([label, value]) => (
          <div key={label} className="rounded-card bg-white p-4">
            <div className="text-[13px] text-muted">{label}</div>
            <div className="mt-0.5 text-[26px] font-medium">{value}</div>
          </div>
        ))}
      </div>

      <Card>
        <CardTitle className="mb-1.5">{d.unitsTitle}</CardTitle>
        <p className="m-0 mb-3 text-[13px] text-muted">
          {d.unitsHint}
        </p>
        <div className="overflow-x-auto">
          <div className="min-w-160">
            <div className={cn(TABLE_COLS, "border-b border-line py-2 text-xs text-faint")}>
              <span>{d.cols.code}</span>
              <span>{d.cols.registration}</span>
              <span>{d.cols.mileage}</span>
              <span>{d.cols.status}</span>
              <span>{d.cols.occupation}</span>
              <span />
            </div>
            {units.map((u) => (
              <div key={u.id} className={cn(TABLE_COLS, "items-center border-b border-surface py-3 text-[13px]")}>
                <span className="font-mono font-medium">{u.code}</span>
                <span>{u.plate}</span>
                <span className="text-muted">{num(u.mileage)} km</span>
                <span>
                  <StatusBadge kind="unit" status={u.status} />
                </span>
                <span className="text-ink-soft">{occupation(u)}</span>
                <span className="flex justify-end gap-1.5">
                  <ActionButton
                    size="sm"
                    className="font-normal"
                    onClick={() => {
                      const back = u.status === "maintenance";
                      run(setUnitStatus, u.id, back ? "active" : "maintenance");
                      toast(back ? d.backInService(u.code) : d.nowInMaintenance(u.code));
                    }}
                  >
                    {u.status === "maintenance" ? d.returnToService : d.maintenance}
                  </ActionButton>
                  <ActionButton size="sm" className="font-normal" onClick={() => setBlockUnit(u)}>
                    {d.blockDates}
                  </ActionButton>
                </span>
              </div>
            ))}
          </div>
        </div>
        <form
          className="mt-3.5 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <input
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            placeholder={d.codePlaceholder(nextCode)}
            aria-label={t.company.editor.code}
            className="h-9.5 rounded border-0 bg-surface px-3 font-mono text-[13px] outline-none focus:ring-1 focus:ring-brand"
          />
          <input
            value={newPlate}
            onChange={(e) => setNewPlate(e.target.value)}
            placeholder={d.cols.registration}
            aria-label={d.cols.registration}
            className="h-9.5 rounded border-0 bg-surface px-3 text-[13px] outline-none focus:ring-1 focus:ring-brand"
          />
          <ActionButton type="submit" variant="dark" className="h-9.5" disabled={adding}>
            {d.addUnit}
          </ActionButton>
        </form>
      </Card>

      <Card className="mt-4">
        <CardTitle>{d.bookingsTitle}</CardTitle>
        {bookings.map((b) => (
          <Link
            key={b.id}
            to={`/company/bookings/${b.id}`}
            className="flex flex-wrap items-center gap-3 border-t border-[#F0F0F0] py-2.5 text-[13px] text-ink no-underline"
          >
            <span className="min-w-22.5 font-mono">{b.id}</span>
            <span className="min-w-35 flex-1">
              {driverName(findDriver(state, b.driverId))} · {shortDate(b.start)} → {shortDate(b.end)}
            </span>
            <span className="text-muted">{b.unitId ? findUnit(state, b.unitId)?.code : d.notAssigned}</span>
            <StatusBadge kind="booking" status={b.status} />
          </Link>
        ))}
        {bookings.length === 0 && <p className="m-0 text-[13px] text-faint">{d.noBookings}</p>}
      </Card>

      {blockUnit && <BlockDatesModal unit={blockUnit} onClose={() => setBlockUnit(null)} />}
      {publishModal}
    </>
  );
}
